import { graphql, type HeadProps, type PageProps } from 'gatsby'
import React from 'react'

import SliderHome from '@/components/blocks-library/slide/slide'
import SectionBlocks from '@/components/section-blocks/section-blocks'
import { Microdata } from '@/components/seo/microdata'
import Seo from '@/components/seo/seo'
import Layout from '@/layouts/layout'

type DataProps = {
  datoCmsPctHome: Queries.HomeQuery['datoCmsPctHome']
  allDatoCmsPctPost: Queries.HomeQuery['allDatoCmsPctPost']
  allDatoCmsPctInterview: Queries.HomeQuery['allDatoCmsPctInterview']
  allDatoCmsPctPaper: Queries.HomeQuery['allDatoCmsPctPaper']
  allDatoCmsPctDialog: Queries.HomeQuery['allDatoCmsPctDialog']
  allDatoCmsPctProject: Queries.HomeQuery['allDatoCmsPctProject']
  allDatoCmsPctEpisode: Queries.HomeQuery['allDatoCmsPctEpisode']
  allDatoCmsPctPage: Queries.HomeQuery['allDatoCmsPctPage']
  allDatoCmsPctExternalContent: Queries.HomeQuery['allDatoCmsPctExternalContent']
  allDatoCmsPctEvent: Queries.HomeQuery['allDatoCmsPctEvent']
}

export const Head: React.FC<HeadProps<DataProps>> = ({
  data: { datoCmsPctHome },
  location
}) => {
  return (
    <>
      <Seo seo={datoCmsPctHome?.seoMetaTags} location={location} />
      <Microdata data={datoCmsPctHome} location={location} />
    </>
  )
}

const Home: React.FC<PageProps<DataProps, HomeCreatorPageContext>> = ({
  data: {
    datoCmsPctHome,
    allDatoCmsPctPost,
    allDatoCmsPctInterview,
    allDatoCmsPctPaper,
    allDatoCmsPctDialog,
    allDatoCmsPctProject,
    allDatoCmsPctEpisode,
    allDatoCmsPctPage,
    allDatoCmsPctEvent,
    allDatoCmsPctExternalContent
  },
  pageContext,
  location
}) => {
  if (!datoCmsPctHome) {
    return <></>
  }
  const nodes = [
    ...allDatoCmsPctPost.nodes,
    ...allDatoCmsPctInterview.nodes,
    ...allDatoCmsPctProject.nodes,
    ...allDatoCmsPctPaper.nodes,
    ...allDatoCmsPctDialog.nodes,
    ...allDatoCmsPctEpisode.nodes,
    ...allDatoCmsPctPage.nodes,
    ...allDatoCmsPctEvent.nodes,
    ...allDatoCmsPctExternalContent.nodes
  ]

  const { slide, blocks } = datoCmsPctHome
  return (
    <Layout location={location} shareButtonsScrollTop={450}>
      {slide && <SliderHome slides={slide as Queries.SlideFragment[]} />}
      <SectionBlocks blocks={blocks} pageContext={pageContext} nodes={nodes} />
    </Layout>
  )
}

export default Home

export const query = graphql`
  query Home($allNodesIds: [String] = "") {
    datoCmsPctHome {
      ...PctHome
    }

    allDatoCmsPctPost(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultPost
      }
    }
    allDatoCmsPctInterview(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultInterview
      }
    }
    allDatoCmsPctPaper(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultPaper
      }
    }
    allDatoCmsPctDialog(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultDialog
      }
    }
    allDatoCmsPctProject(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultProject
      }
    }
    allDatoCmsPctEpisode(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultEpisode
      }
    }
    allDatoCmsPctPage(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultPage
      }
    }
    allDatoCmsPctExternalContent(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultExternalContent
      }
    }

    allDatoCmsPctEvent(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultEvent
      }
    }
  }
  fragment PctHome on DatoCmsPctHome {
    __typename
    originalId
    meta {
      firstPublishedAt
    }
    seoMetaTags {
      ...GatsbyDatoCmsSeoMetaTags
    }
    id
    slide {
      ...Slide
    }
    blocks: section {
      __typename
      ... on DatoCmsFeaturedContent {
        ...FeaturedContent
      }
      ... on DatoCmsFeaturedPaper {
        ...SectionFeaturedPaper
      }
      ... on DatoCmsFeaturedExploreTopic {
        ...SectionFeaturedExploreTopic
      }
      ... on DatoCmsFeaturedMultipleContent {
        ...SectionFeaturedContents
      }
      ... on DatoCmsFeaturedStatic {
        ...FeaturedStatic
      }
      ... on DatoCmsFeaturedMultiplePaper {
        ...SectionFeaturedPapers
      }
      ... on DatoCmsFeaturedLeader {
        ...FeaturedLeader
      }
    }
  }
`
